/**
 * Regulatory filing workspace data access (migration 0008; Master Plan Argus
 * Phase 3 — "Deadline-driven filings, AI draft → filing-ready").
 *
 * All queries run client-side under the signed-in user's RLS (ADR-0004 MVP
 * pipeline pattern). The deterministic auto-population (engine) runs in the
 * browser at create time; the AI drafting goes through the `draft-filing` edge
 * function (UI never calls Anthropic directly). Every value is reviewable: AI
 * and auto drafts land `unreviewed` and require human sign-off (build doc §1.4).
 */
import { supabase } from "@/lib/supabase/client";
import {
  autoPopulateFormAdv,
  autoPopulateFormPf,
  buildFormAdvFields,
  buildFormPfFields,
  deterministicFormAdvDrafts,
  deterministicFormPfDrafts,
  FORM_ADV_MODEL_VERSION,
  FORM_PF_MODEL_VERSION,
  type CanonicalCommitment,
  type CanonicalFund,
  type CanonicalLp,
  type FieldValueDraft,
  type FormPfFieldDef,
} from "@argus/engine";

export type FilingFormType = "form_pf" | "form_adv";
export type FilingStatus = "draft" | "in_review" | "ready" | "filed" | "withdrawn";
export type FieldSource = "auto" | "ai" | "manual";
export type FieldReviewStatus = "unreviewed" | "accepted" | "edited" | "flagged";

export interface FilingRow {
  id: string;
  tenant_id: string;
  form_type: FilingFormType;
  title: string;
  compliance_task_id: string | null;
  fund_id: string | null;
  period_label: string | null;
  period_start: string | null;
  period_end: string | null;
  fiscal_year_end: string | null;
  due_date: string | null;
  status: FilingStatus;
  engine_version: string | null;
  prepared_by: string | null;
  filed_at: string | null;
  filed_by: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface FilingFieldRow {
  id: string;
  tenant_id: string;
  filing_id: string;
  field_key: string;
  section: string;
  value: unknown;
  source: FieldSource;
  is_gap: boolean;
  confidence: number | null;
  citation: string | null;
  review_status: FieldReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  notes: string | null;
}

export interface FilingEventRow {
  id: string;
  filing_id: string;
  event_type: string;
  field_key: string | null;
  from_status: string | null;
  to_status: string | null;
  note: string | null;
  actor_id: string | null;
  created_at: string;
}

const FILING_COLS =
  "id, tenant_id, form_type, title, compliance_task_id, fund_id, period_label, period_start, period_end, fiscal_year_end, due_date, status, engine_version, prepared_by, filed_at, filed_by, notes, metadata, created_at, updated_at";
const FIELD_COLS =
  "id, tenant_id, filing_id, field_key, section, value, source, is_gap, confidence, citation, review_status, reviewed_by, reviewed_at, notes";

/* ------------------------------------------------------------------ Filings */

export async function listFilings(tenantId: string): Promise<FilingRow[]> {
  const { data, error } = await supabase
    .from("filings")
    .select(FILING_COLS)
    .eq("tenant_id", tenantId)
    .order("due_date", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: false });
  if (error) throw new Error(`listFilings: ${error.message}`);
  return data ?? [];
}

export async function getFiling(id: string): Promise<FilingRow> {
  const { data, error } = await supabase.from("filings").select(FILING_COLS).eq("id", id).single();
  if (error) throw new Error(`getFiling: ${error.message}`);
  return data;
}

export async function listFilingFields(filingId: string): Promise<FilingFieldRow[]> {
  const { data, error } = await supabase
    .from("filing_field_values")
    .select(FIELD_COLS)
    .eq("filing_id", filingId)
    .order("section", { ascending: true });
  if (error) throw new Error(`listFilingFields: ${error.message}`);
  return data ?? [];
}

export async function listFilingEvents(filingId: string): Promise<FilingEventRow[]> {
  const { data, error } = await supabase
    .from("filing_events")
    .select(
      "id, filing_id, event_type, field_key, from_status, to_status, note, actor_id, created_at",
    )
    .eq("filing_id", filingId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(`listFilingEvents: ${error.message}`);
  return data ?? [];
}

/* --------------------------------------------------- Canonical context load */

async function loadCanonicalContext(tenantId: string): Promise<{
  firmName: string;
  funds: CanonicalFund[];
  lps: CanonicalLp[];
  commitments: CanonicalCommitment[];
}> {
  const [tenantRow, fundsRes, lpsRes, commitRes] = await Promise.all([
    fetchFirmRow(tenantId),
    supabase
      .from("funds")
      .select(
        "id, tenant_id, name, short_name, vintage_year, fund_size_usd, domicile, structure, strategy, status",
      )
      .eq("tenant_id", tenantId),
    supabase
      .from("lps")
      .select("id, tenant_id, legal_name, short_name, lp_type, domicile")
      .eq("tenant_id", tenantId),
    supabase
      .from("fund_lp_commitments")
      .select("id, tenant_id, fund_id, lp_id, commitment_usd, closing_date, closing_round, status")
      .eq("tenant_id", tenantId),
  ]);
  if (fundsRes.error) throw new Error(`context/funds: ${fundsRes.error.message}`);
  if (lpsRes.error) throw new Error(`context/lps: ${lpsRes.error.message}`);
  if (commitRes.error) throw new Error(`context/commitments: ${commitRes.error.message}`);

  // Map DB rows → the engine's canonical shapes (mirrors @argus/shared-types
  // adapters; kept inline so the web app depends only on @argus/engine).
  const funds: CanonicalFund[] = (
    (fundsRes.data ?? []) as {
      id: string;
      tenant_id: string;
      name: string;
      short_name: string | null;
      vintage_year: number | null;
      fund_size_usd: number | string | null;
      domicile: string | null;
      structure: string | null;
      strategy: string | null;
      status: string;
    }[]
  ).map((r) => ({
    id: r.id,
    tenantId: r.tenant_id,
    name: r.name,
    shortName: r.short_name,
    vintageYear: r.vintage_year,
    size: r.fund_size_usd === null ? null : Number(r.fund_size_usd),
    currency: "USD",
    domicile: r.domicile,
    structure: (r.structure as CanonicalFund["structure"]) ?? null,
    strategy: (r.strategy as CanonicalFund["strategy"]) ?? null,
    status: r.status as CanonicalFund["status"],
  }));

  const lps: CanonicalLp[] = (
    (lpsRes.data ?? []) as {
      id: string;
      tenant_id: string;
      legal_name: string;
      short_name: string | null;
      lp_type: string | null;
      domicile: string | null;
    }[]
  ).map((r) => ({
    id: r.id,
    tenantId: r.tenant_id,
    legalName: r.legal_name,
    shortName: r.short_name,
    type: (r.lp_type as CanonicalLp["type"]) ?? null,
    domicile: r.domicile,
  }));

  const commitments: CanonicalCommitment[] = (
    (commitRes.data ?? []) as {
      id: string;
      tenant_id: string;
      fund_id: string;
      lp_id: string;
      commitment_usd: number | string;
      closing_date: string | null;
      closing_round: string | null;
      status: string;
    }[]
  ).map((r) => ({
    id: r.id,
    tenantId: r.tenant_id,
    fundId: r.fund_id,
    lpId: r.lp_id,
    amount: Number(r.commitment_usd),
    currency: "USD",
    closingDate: r.closing_date,
    closingRound: r.closing_round,
    status: r.status as CanonicalCommitment["status"],
  }));

  return { firmName: tenantRow.name, funds, lps, commitments };
}

/** Typed-return boundary so the untyped supabase result never leaks `any` into
 * member access (mirrors getFiling / getMfnTrigger). */
async function fetchFirmRow(tenantId: string): Promise<{ name: string }> {
  const { data, error } = await supabase.from("tenants").select("name").eq("id", tenantId).single();
  if (error) throw new Error(`context/tenant: ${error.message}`);
  return data;
}

/** The Form PF field definitions for a filing's funds, keyed by expanded field
 * key. The workspace uses this to render labels / items / help per field. */
export function formPfFieldDefs(
  funds: { id: string }[],
): Map<string, FormPfFieldDef & { fundId: string | null }> {
  return new Map(buildFormPfFields(funds).map((f) => [f.key, f]));
}

/** The Form ADV field definitions for a filing's funds, keyed by expanded key. */
export function formAdvFieldDefs(
  funds: { id: string }[],
): Map<string, FormPfFieldDef & { fundId: string | null }> {
  return new Map(buildFormAdvFields(funds).map((f) => [f.key, f]));
}

/** Field definitions for whichever form this filing is. */
export function fieldDefsForForm(
  formType: FilingFormType,
  funds: { id: string }[],
): Map<string, FormPfFieldDef & { fundId: string | null }> {
  return formType === "form_adv" ? formAdvFieldDefs(funds) : formPfFieldDefs(funds);
}

/* -------------------------------------------------------- Create a Form PF */

export interface CreateFormPfInput {
  tenantId: string;
  periodLabel: string;
  fiscalYearEnd?: string | null;
  dueDate?: string | null;
  complianceTaskId?: string | null;
  userId: string;
}

/**
 * Create a Form PF filing and auto-populate every field Argus can know from the
 * tenant's funds/LPs/commitments. Idempotent on (tenant, form_type, period): if a
 * filing already exists for the period it is returned unchanged.
 */
export async function createFormPfFiling(input: CreateFormPfInput): Promise<FilingRow> {
  const existing = await supabase
    .from("filings")
    .select(FILING_COLS)
    .eq("tenant_id", input.tenantId)
    .eq("form_type", "form_pf")
    .eq("period_label", input.periodLabel)
    .maybeSingle();
  if (existing.error) throw new Error(`createFormPfFiling/check: ${existing.error.message}`);
  if (existing.data) return existing.data;

  const ctx = await loadCanonicalContext(input.tenantId);

  const filing = await insertFilingRow({
    tenant_id: input.tenantId,
    form_type: "form_pf",
    title: `Form PF — ${input.periodLabel}`,
    compliance_task_id: input.complianceTaskId ?? null,
    period_label: input.periodLabel,
    fiscal_year_end: input.fiscalYearEnd ?? null,
    due_date: input.dueDate ?? null,
    status: "draft",
    engine_version: FORM_PF_MODEL_VERSION,
    prepared_by: input.userId,
    created_by: input.userId,
  });

  const drafts = autoPopulateFormPf(ctx);
  const rows = drafts.map((d: FieldValueDraft) => ({
    tenant_id: input.tenantId,
    filing_id: filing.id,
    field_key: d.fieldKey,
    section: d.section,
    value: d.value,
    source: d.source,
    is_gap: d.isGap,
    confidence: d.confidence,
    citation: d.citation,
  }));
  const { error: fieldsErr } = await supabase.from("filing_field_values").insert(rows);
  if (fieldsErr) throw new Error(`createFormPfFiling/fields: ${fieldsErr.message}`);

  const autoCount = drafts.filter((d) => d.source === "auto" && d.value !== null).length;
  const gapCount = drafts.filter((d) => d.isGap).length;
  await recordEvent(input.tenantId, filing.id, "created", input.userId, {
    note: `Form PF initialized — ${autoCount} fields auto-populated, ${gapCount} gaps flagged.`,
  });
  return filing;
}

/* -------------------------------------------------------- Create a Form ADV */

/**
 * Create a Form ADV Part 1A filing and auto-populate every field Argus can know
 * (Item 1, Item 5, Schedule D 7.B.1). Same substrate + provenance as Form PF
 * (ADR-0006: a new model, not a new table). Idempotent on (tenant, form, period).
 */
export async function createFormAdvFiling(input: CreateFormPfInput): Promise<FilingRow> {
  const existing = await supabase
    .from("filings")
    .select(FILING_COLS)
    .eq("tenant_id", input.tenantId)
    .eq("form_type", "form_adv")
    .eq("period_label", input.periodLabel)
    .maybeSingle();
  if (existing.error) throw new Error(`createFormAdvFiling/check: ${existing.error.message}`);
  if (existing.data) return existing.data;

  const ctx = await loadCanonicalContext(input.tenantId);

  const filing = await insertFilingRow({
    tenant_id: input.tenantId,
    form_type: "form_adv",
    title: `Form ADV — ${input.periodLabel}`,
    compliance_task_id: input.complianceTaskId ?? null,
    period_label: input.periodLabel,
    fiscal_year_end: input.fiscalYearEnd ?? null,
    due_date: input.dueDate ?? null,
    status: "draft",
    engine_version: FORM_ADV_MODEL_VERSION,
    prepared_by: input.userId,
    created_by: input.userId,
  });

  const drafts = autoPopulateFormAdv(ctx);
  const rows = drafts.map((d: FieldValueDraft) => ({
    tenant_id: input.tenantId,
    filing_id: filing.id,
    field_key: d.fieldKey,
    section: d.section,
    value: d.value,
    source: d.source,
    is_gap: d.isGap,
    confidence: d.confidence,
    citation: d.citation,
  }));
  const { error: fieldsErr } = await supabase.from("filing_field_values").insert(rows);
  if (fieldsErr) throw new Error(`createFormAdvFiling/fields: ${fieldsErr.message}`);

  const autoCount = drafts.filter((d) => d.source === "auto" && d.value !== null).length;
  const gapCount = drafts.filter((d) => d.isGap).length;
  await recordEvent(input.tenantId, filing.id, "created", input.userId, {
    note: `Form ADV initialized — ${autoCount} fields auto-populated, ${gapCount} gaps flagged.`,
  });
  return filing;
}

/* --------------------------------------------------------- Field operations */

export async function saveFieldValue(
  field: FilingFieldRow,
  patch: { value?: unknown; reviewStatus?: FieldReviewStatus; notes?: string | null },
  userId: string,
): Promise<void> {
  const now = new Date().toISOString();
  const update: Record<string, unknown> = {};
  let edited = false;
  if (patch.value !== undefined) {
    update.value = patch.value;
    update.is_gap = patch.value === null || patch.value === "";
    edited = true;
  }
  if (patch.reviewStatus) {
    update.review_status = patch.reviewStatus;
    update.reviewed_by = userId;
    update.reviewed_at = now;
  } else if (edited) {
    // Editing a value is itself a human sign-off.
    update.review_status = "edited";
    update.reviewed_by = userId;
    update.reviewed_at = now;
  }
  if (patch.notes !== undefined) update.notes = patch.notes;

  const { error } = await supabase.from("filing_field_values").update(update).eq("id", field.id);
  if (error) throw new Error(`saveFieldValue: ${error.message}`);

  await recordEvent(
    field.tenant_id,
    field.filing_id,
    edited ? "field_edited" : "field_reviewed",
    userId,
    {
      fieldKey: field.field_key,
      note: patch.reviewStatus ? `marked ${patch.reviewStatus}` : undefined,
    },
  );
}

/** Accept an auto/ai value as-is (human sign-off without changing the value). */
export async function acceptField(field: FilingFieldRow, userId: string): Promise<void> {
  await saveFieldValue(field, { reviewStatus: "accepted" }, userId);
}

/* ----------------------------------------------------------- AI drafting */

interface DraftResponse {
  engine: string;
  configured: boolean;
  fallback?: string;
  drafts: {
    fieldKey: string;
    section: string;
    value: unknown;
    confidence: number;
    citation: string;
  }[];
}

export interface DraftResult {
  engine: string;
  configured: boolean;
  applied: number;
  fallback?: string;
}

/**
 * Try the draft-filing edge function (Claude path, or its server-side
 * deterministic preview when no key is set). Returns null when the function is
 * not deployed or unreachable — the caller falls back to the in-browser engine,
 * mirroring the extraction pipeline (run-extraction.ts). The UI never talks to
 * Anthropic directly (anti-pattern §17.6).
 */
async function tryDraftViaEdge(filingId: string): Promise<DraftResponse | null> {
  try {
    const { data, error } = (await supabase.functions.invoke<DraftResponse>("draft-filing", {
      body: { filing_id: filingId },
    })) as { data: DraftResponse | null; error: { message: string } | null };
    if (error || !data || !Array.isArray(data.drafts)) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * In-browser deterministic fallback for {@link draftFilingWithAI}: computes the
 * same drafts the edge function would produce without a model key, using the
 * engine. Runs under the caller's RLS. Keeps "Draft with AI" fully functional in
 * any environment where the edge function isn't deployed.
 */
async function draftOffline(filing: FilingRow): Promise<DraftResponse> {
  const [ctx, fields] = await Promise.all([
    loadCanonicalContext(filing.tenant_id),
    listFilingFields(filing.id),
  ]);
  const aiFieldKeys = fields.filter((f) => f.source === "ai").map((f) => f.field_key);
  const period = filing.period_label ?? "the reporting period";
  const drafts =
    filing.form_type === "form_adv"
      ? deterministicFormAdvDrafts({
          firmName: ctx.firmName,
          period,
          funds: ctx.funds,
          commitments: ctx.commitments,
          aiFieldKeys,
        })
      : deterministicFormPfDrafts({
          firmName: ctx.firmName,
          period,
          funds: ctx.funds,
          commitments: ctx.commitments,
          aiFieldKeys,
        });
  const modelVersion =
    filing.form_type === "form_adv" ? FORM_ADV_MODEL_VERSION : FORM_PF_MODEL_VERSION;
  return {
    engine: `deterministic/${modelVersion}`,
    configured: false,
    fallback: "draft-filing edge function unavailable — drafted in-browser with the engine.",
    drafts: drafts.map((d) => ({
      fieldKey: d.fieldKey,
      section: d.section,
      value: d.value,
      confidence: d.confidence,
      citation: d.citation,
    })),
  };
}

/**
 * Draft the filing's `ai` fields and apply the results. Always completes: prefers
 * the draft-filing edge function (Claude when keyed, deterministic preview
 * otherwise) and falls back to the in-browser deterministic engine when the
 * function isn't deployed. Drafts land `unreviewed` for human sign-off (§1.4).
 */
export async function draftFilingWithAI(filing: FilingRow, userId: string): Promise<DraftResult> {
  const resp: DraftResponse = (await tryDraftViaEdge(filing.id)) ?? (await draftOffline(filing));

  let applied = 0;
  for (const d of resp.drafts) {
    const { error: upErr } = await supabase
      .from("filing_field_values")
      .update({
        value: d.value,
        source: "ai",
        is_gap: false,
        confidence: d.confidence,
        citation: d.citation,
        review_status: "unreviewed",
      })
      .eq("filing_id", filing.id)
      .eq("field_key", d.fieldKey);
    if (!upErr) applied += 1;
  }

  await recordEvent(filing.tenant_id, filing.id, "ai_drafted", userId, {
    note: `${resp.configured ? "Claude" : "Deterministic"} draft applied to ${applied} field(s) [${resp.engine}].`,
  });

  const result: DraftResult = { engine: resp.engine, configured: resp.configured, applied };
  if (resp.fallback) result.fallback = resp.fallback;
  return result;
}

/* --------------------------------------------------------- Status workflow */

const STATUS_TERMINAL: FilingStatus[] = ["filed", "withdrawn"];

export async function setFilingStatus(
  filing: FilingRow,
  to: FilingStatus,
  userId: string,
  note?: string,
): Promise<void> {
  const patch: Record<string, unknown> = { status: to };
  if (to === "filed") {
    patch.filed_at = new Date().toISOString();
    patch.filed_by = userId;
  }
  if (!STATUS_TERMINAL.includes(to) && filing.filed_at) {
    patch.filed_at = null;
    patch.filed_by = null;
  }
  const { error } = await supabase.from("filings").update(patch).eq("id", filing.id);
  if (error) throw new Error(`setFilingStatus: ${error.message}`);

  const eventType =
    to === "filed" ? "filed" : filing.status === "filed" ? "reopened" : "status_change";
  await recordEvent(filing.tenant_id, filing.id, eventType, userId, {
    fromStatus: filing.status,
    toStatus: to,
    note,
  });
}

export async function deleteFiling(id: string): Promise<void> {
  const { error } = await supabase.from("filings").delete().eq("id", id);
  if (error) throw new Error(`deleteFiling: ${error.message}`);
}

/** Record that the filing was exported (for the append-only evidence trail). */
export async function recordFilingExport(filing: FilingRow, userId: string): Promise<void> {
  await recordEvent(filing.tenant_id, filing.id, "exported", userId, {
    note: "Filing-ready package exported.",
  });
}

/* ----------------------------------------------------------------- internal */

/** Insert a filing and return the typed row (typed-return boundary keeps the
 * untyped supabase `any` out of member access at the call site). */
async function insertFilingRow(payload: Record<string, unknown>): Promise<FilingRow> {
  const { data, error } = await supabase
    .from("filings")
    .insert(payload)
    .select(FILING_COLS)
    .single();
  if (error) throw new Error(`createFormPfFiling: ${error.message}`);
  return data;
}

async function recordEvent(
  tenantId: string,
  filingId: string,
  eventType: string,
  userId: string,
  opts: {
    fieldKey?: string | undefined;
    fromStatus?: string | undefined;
    toStatus?: string | undefined;
    note?: string | undefined;
  } = {},
): Promise<void> {
  const { error } = await supabase.from("filing_events").insert({
    tenant_id: tenantId,
    filing_id: filingId,
    event_type: eventType,
    field_key: opts.fieldKey ?? null,
    from_status: opts.fromStatus ?? null,
    to_status: opts.toStatus ?? null,
    note: opts.note ?? null,
    actor_id: userId,
  });
  // Events are best-effort audit; never block the primary action on a log write.
  if (error) console.warn(`filing_event(${eventType}): ${error.message}`);
}
